import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type ActionKeys,
	SCHEDULED_CHAT_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	convertToBackendPayload,
	convertFromBackendPayload,
	deleteStudioSc3Rule,
	fetchScheduledChatStudioInfo,
	fetchStudioSc3Rule,
	buildScheduledChatStudioSc3RuleJsonPatch,
	patchStudioSc3RuleJsonPatch,
	type ScheduledChatBackendPayload,
	SCHEDULED_CHAT_DAYS,
	type ScheduledChatDayOfWeek,
	type ScheduledChatFrequency,
	type ScheduledChatPayload,
} from '@atlassian/conversation-assistant-message-actions-api/scheduled-chat';
import {
	type ActionRendererFor,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { ScheduledChatPreviewCard } from '@atlassian/conversation-assistant-preview-card/scheduled-chats-preview-card';
import { SystemMessage } from '@atlassian/conversation-assistant-preview-card/system-message';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import { deduceNextRunAtIsoFromRRuleRaw } from './deduce-next-run-at-from-rrule';
import messages from './messages';
import { ScheduledChatEditorModal } from './ScheduledChatEditorModal';
import { ScheduledChatFormFields } from './ScheduledChatFormFields';
import type { ScheduledChatEditorSubmitContext } from './types';
import { frequencyLabelFor } from './utils';

const DAY_ORDER = SCHEDULED_CHAT_DAYS;
const SCHEDULED_CHAT_INTERACTION_SOURCE = 'scheduledChatRenderer';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
});

export const filterScheduledChatActions = (
	actions: MessageAction[],
): ActionWith<ScheduledChatBackendPayload>[] => {
	const filtered = actions.filter((action) => {
		if (action.key !== SCHEDULED_CHAT_KEY) {
			throw new Error('Invalid action key for ScheduledChatAction');
		}
		return true;
	});
	return filtered as ActionWith<ScheduledChatBackendPayload>[];
};

type ScheduledChatActionFooterProps = {
	isSubmitting: boolean;
	isSaveDisabled: boolean;
	onSave: () => void;
};

const ScheduledChatActionFooter = ({
	isSubmitting,
	isSaveDisabled,
	onSave,
}: ScheduledChatActionFooterProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.footer}>
			<Button
				appearance="primary"
				onClick={onSave}
				isLoading={isSubmitting}
				isDisabled={isSaveDisabled}
				testId="scheduled-chat-create"
			>
				{formatMessage(messages.createButton)}
			</Button>
		</Box>
	);
};

export const ScheduledChatAction = (
	props: ActionRendererFor<typeof ActionKeys.ScheduledChat>,
): JSX.Element => {
	const { formatMessage, formatDate } = useIntl();
	const {
		actions,
		getReadonly,
		getStatus,
		onCustomDataConfirm,
		productKey,
		cloudId,
		experienceId,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
	} = props;

	const firstAction = actions[0];
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const isActionLoading = status !== 'idle';
	const isSubmitting = status === 'started';
	const isFinished = status === 'finished' || firstAction.status === 'finished';

	const data = firstAction.data;

	const frontendData = useMemo(() => convertFromBackendPayload(data), [data]);

	const [name, setName] = useState(frontendData.name);
	const [prompt, setPrompt] = useState(frontendData.prompt);
	const [frequency, setFrequency] = useState<ScheduledChatFrequency>(frontendData.frequency);
	const [daysOfWeek, setDaysOfWeek] = useState<ScheduledChatDayOfWeek[]>(
		frontendData.daysOfWeek.length ? frontendData.daysOfWeek : [DAY_ORDER[0]],
	);
	const [time, setTime] = useState(frontendData.time);
	const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
	const [isPaused, setIsPaused] = useState(false);

	const [studioWorkspaceId, setStudioWorkspaceId] = useState<string | undefined>(undefined);
	const [studioSpaceId, setStudioSpaceId] = useState<string | undefined>(undefined);

	// Fetch once on mount (or if product/site context changes) to resolve
	// the Studio workspace and space IDs needed for edit/delete operations.
	useEffect(() => {
		if (!experienceId) {
			return;
		}
		let cancelled = false;
		void fetchScheduledChatStudioInfo({
			productHeader: productKey,
			experienceId,
			cloudId,
		}).then((info) => {
			if (!cancelled && info) {
				setStudioWorkspaceId(info.workspaceId);
				setStudioSpaceId(info.spaceId);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [productKey, cloudId, experienceId]);

	const selectedDays = useMemo(
		() => (frequency === 'custom' ? daysOfWeek : daysOfWeek.slice(0, 1)),
		[daysOfWeek, frequency],
	);
	const currentFrontendPayload = useMemo(
		(): ScheduledChatPayload => ({
			name: name.trim(),
			prompt: prompt.trim(),
			frequency,
			daysOfWeek: selectedDays,
			time,
		}),
		[frequency, name, prompt, selectedDays, time],
	);
	const currentBackendPayload = useMemo(() => {
		try {
			return convertToBackendPayload(currentFrontendPayload);
		} catch {
			return null;
		}
	}, [currentFrontendPayload]);

	const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);
	const [isDeletingScheduledChat, setIsDeletingScheduledChat] = useState(false);

	const studioRuleId = useMemo(() => {
		const raw = firstAction.data.ruleIdUuid;
		return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : undefined;
	}, [firstAction.data]);

	const harvestedRRule = useMemo(() => {
		const raw = firstAction.data.rRule;
		return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : undefined;
	}, [firstAction.data]);

	const openDeleteModal = useCallback(() => {
		if (!studioRuleId) {
			return;
		}
		setPendingDelete({ id: studioRuleId, title: currentFrontendPayload.name });
	}, [studioRuleId, currentFrontendPayload.name]);

	const closeDeleteModal = useCallback(() => {
		if (!isDeletingScheduledChat) {
			setPendingDelete(null);
		}
	}, [isDeletingScheduledChat]);

	const confirmDelete = useCallback(() => {
		const target = pendingDelete;
		if (!target) {
			return;
		}
		if (studioWorkspaceId === undefined || studioSpaceId === undefined) {
			return;
		}
		const workspaceId = studioWorkspaceId;
		const spaceId = studioSpaceId;
		void (async () => {
			setIsDeletingScheduledChat(true);
			try {
				const res = await deleteStudioSc3Rule({
					workspaceId,
					spaceId,
					ruleId: target.id,
				});
				if (!res.ok) {
					return;
				}
				setPendingDelete(null);
			} catch {
				// TODO: Add error boundary
			} finally {
				setIsDeletingScheduledChat(false);
			}
		})();
	}, [pendingDelete, studioWorkspaceId, studioSpaceId]);

	const isSaveDisabled =
		readonly ||
		isActionLoading ||
		!name.trim() ||
		!prompt.trim() ||
		(frequency !== 'hourly' && !time.trim()) ||
		(frequency === 'custom' && daysOfWeek.length === 0);

	const handleSave = useCallback(async () => {
		if (!currentBackendPayload) {
			return;
		}

		try {
			if (fg('rovo_tools_resumption_migration_phase2')) {
				const plannedActions = actions.filter((action) => getStatus(action) === 'idle');
				sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: SCHEDULED_CHAT_INTERACTION_SOURCE,
					},
					confirmed_tools: {
						message_id: messageId,
						tools: plannedActions.map((action) =>
							action.invocationId === firstAction.invocationId
								? buildToolFromAction(action, 'confirmed', currentBackendPayload)
								: buildToolFromAction(action, 'cancelled'),
						),
					},
				});
				return;
			}

			// Rule creation is performed by the message-action / assist resolution path; we only confirm the payload.
			await onCustomDataConfirm(firstAction, currentBackendPayload);
		} catch (error) {
			throw new Error(
				`Failed to create scheduled chat: ${
					error instanceof Error ? error.message : 'Unknown error'
				}`,
			);
		}
	}, [
		actions,
		conversationId,
		currentBackendPayload,
		firstAction,
		getStatus,
		messageId,
		onCustomDataConfirm,
		recipientAgentId,
		sendMessageConfirmedTools,
	]);

	const [isSavingEdit, setIsSavingEdit] = useState(false);

	const handleModalSubmit = useCallback(
		(payload: ScheduledChatPayload, context?: ScheduledChatEditorSubmitContext) => {
			const nextPaused = context?.isPaused ?? isPaused;
			const applyLocalState = () => {
				setName(payload.name);
				setPrompt(payload.prompt);
				setFrequency(payload.frequency);
				setDaysOfWeek(payload.daysOfWeek);
				setTime(payload.time);
				setIsPaused(nextPaused);
				setIsEditorModalOpen(false);
			};

			if (!studioRuleId || studioWorkspaceId === undefined || studioSpaceId === undefined) {
				applyLocalState();
				return;
			}

			const workspaceId = studioWorkspaceId;
			const spaceId = studioSpaceId;
			const automationState = context?.isPaused ? 'DISABLED' : 'ENABLED';
			void (async () => {
				setIsSavingEdit(true);
				try {
					const rawRule = await fetchStudioSc3Rule({ workspaceId, spaceId, ruleId: studioRuleId });
					if (rawRule) {
						const patchOps = buildScheduledChatStudioSc3RuleJsonPatch(
							rawRule,
							payload,
							automationState,
						);
						if (patchOps) {
							await patchStudioSc3RuleJsonPatch({
								workspaceId,
								spaceId,
								ruleId: studioRuleId,
								patch: patchOps,
							});
							// TODO: Add error boundary
						}
					}
				} catch {
					// TODO: Add error boundary
				} finally {
					applyLocalState();
					setIsSavingEdit(false);
				}
			})();
		},
		[studioRuleId, studioWorkspaceId, studioSpaceId, isPaused],
	);

	const nextRunIso = useMemo(
		() => deduceNextRunAtIsoFromRRuleRaw(harvestedRRule),
		[harvestedRRule],
	);

	const repeatLabel = useMemo(() => {
		const frequencyText = frequencyLabelFor(currentFrontendPayload.frequency, formatMessage);
		if (currentFrontendPayload.frequency === 'hourly') {
			return frequencyText;
		}
		let timeLabel: string;
		if (nextRunIso) {
			timeLabel = formatDate(nextRunIso, { hour: 'numeric', minute: '2-digit' });
		} else {
			const [hour, minute] = currentFrontendPayload.time
				.split(':')
				.map((v) => Number.parseInt(v, 10));
			const sample = new Date();
			sample.setHours(Number.isFinite(hour) ? hour : 0, Number.isFinite(minute) ? minute : 0, 0, 0);
			timeLabel = formatDate(sample, { hour: 'numeric', minute: '2-digit' });
		}
		return formatMessage(messages.frequencyAtTime, {
			frequency: frequencyText,
			time: timeLabel,
		});
	}, [
		currentFrontendPayload.frequency,
		currentFrontendPayload.time,
		nextRunIso,
		formatMessage,
		formatDate,
	]);

	const nextRunLabel = useMemo(() => {
		if (nextRunIso) {
			const dateLabel = formatDate(nextRunIso, { day: 'numeric', month: 'short' });
			const timeLabel = formatDate(nextRunIso, { hour: 'numeric', minute: '2-digit' });
			return formatMessage(messages.dateAtTime, {
				date: dateLabel,
				time: timeLabel,
			});
		}

		const [hour, minute] = currentFrontendPayload.time
			.split(':')
			.map((v) => Number.parseInt(v, 10));
		const sample = new Date();
		sample.setHours(Number.isFinite(hour) ? hour : 0, Number.isFinite(minute) ? minute : 0, 0, 0);
		return formatDate(sample, { hour: 'numeric', minute: '2-digit' });
	}, [nextRunIso, currentFrontendPayload.time, formatDate, formatMessage]);

	const statusMessage = useMemo(() => {
		if (!currentBackendPayload) {
			throw new Error('No backend payload');
		}
		const formattedData = convertFromBackendPayload(currentBackendPayload);
		if (formattedData.frequency === 'hourly') {
			return `Scheduled ${formattedData.frequency} ${formattedData.name}`;
		}
		return `Scheduled ${formattedData.frequency} ${formattedData.name} at ${formattedData.time}`;
	}, [currentBackendPayload]);

	if (isFinished) {
		return (
			<>
				<SystemMessage message={statusMessage} />
				<ScheduledChatPreviewCard
					title={currentFrontendPayload.name}
					repeatLabel={repeatLabel}
					nextRunLabel={nextRunLabel}
					isSelected={false}
					isEnabled={!isPaused}
					onDeleteClick={openDeleteModal}
					onEditClick={() => {
						setIsEditorModalOpen(true);
					}}
				/>
				<ScheduledChatEditorModal
					isOpen={isEditorModalOpen}
					mode="edit"
					initialPayload={currentFrontendPayload}
					initialPaused={isPaused}
					rRule={harvestedRRule}
					isSubmitting={isSavingEdit}
					readonly={false}
					onClose={() => setIsEditorModalOpen(false)}
					onSubmit={handleModalSubmit}
					onDelete={(payload) => {
						setIsEditorModalOpen(false);
						openDeleteModal();
					}}
				/>
				<ModalTransition>
					{pendingDelete ? (
						<Modal
							shouldCloseOnEscapePress={!isDeletingScheduledChat}
							shouldCloseOnOverlayClick={!isDeletingScheduledChat}
							onClose={closeDeleteModal}
						>
							<ModalHeader hasCloseButton={!isDeletingScheduledChat}>
								<ModalTitle>{formatMessage(messages.deleteModalTitle)}</ModalTitle>
							</ModalHeader>
							<ModalBody>
								<Text>
									{formatMessage(messages.deleteModalDescription, {
										name: pendingDelete.title,
									})}
								</Text>
							</ModalBody>
							<ModalFooter>
								<Inline space="space.100">
									<Button
										appearance="subtle"
										spacing="default"
										onClick={closeDeleteModal}
										isDisabled={isDeletingScheduledChat}
									>
										{formatMessage(messages.deleteModalCancel)}
									</Button>
									<Button
										appearance="danger"
										spacing="default"
										onClick={confirmDelete}
										isDisabled={isDeletingScheduledChat}
									>
										{formatMessage(messages.deleteModalConfirm)}
									</Button>
								</Inline>
							</ModalFooter>
						</Modal>
					) : null}
				</ModalTransition>
			</>
		);
	}

	return (
		<ActionCardWrapper title={formatMessage(messages.cardTitle)} product="schedule">
			<ScheduledChatFormFields
				formatMessage={formatMessage}
				formatDate={formatDate}
				readonly={readonly}
				isActionLoading={isActionLoading}
				name={name}
				prompt={prompt}
				frequency={frequency}
				daysOfWeek={daysOfWeek}
				time={time}
				onNameChange={setName}
				onPromptChange={setPrompt}
				onFrequencyChange={setFrequency}
				onDaysOfWeekChange={setDaysOfWeek}
				onTimeChange={setTime}
			/>

			<ActionCardFooter>
				<ScheduledChatActionFooter
					isSubmitting={isSubmitting}
					isSaveDisabled={isSaveDisabled}
					onSave={handleSave}
				/>
			</ActionCardFooter>
		</ActionCardWrapper>
	);
};
